// One hook for every modal in the console. The decisions it makes are in
// state/focusTrap.ts and tested there; this is the DOM half.
//
// Three things a modal owes the keyboard:
//   1. focus moves INTO it when it opens, and somewhere useful -- the field it is
//      asking about, not its Close button
//   2. Tab cycles inside it and does not walk into the page behind
//   3. focus goes back where it came from when it closes, so the DM carries on
//      from the row they were on
//
// Escape is handled here too, because a modal that can only be closed by finding
// its Close button with the mouse is the same failure wearing a hat.
import { useEffect, useRef } from "react";
import { canRestore, FOCUSABLE, nextIndex, openFocus } from "../state/focusTrap";

/** `active` is for the common shape in this console: a modal that is rendered
 *  conditionally inside a bigger component, where the hook cannot be. Pass the
 *  same condition the JSX uses and the trap arms and disarms with it.
 *
 *  onClose is read through a ref so an inline arrow -- which is how every caller
 *  writes it -- does not re-run the effect on every render. It would re-steal
 *  focus each time, which from the DM's side is a dialog that will not let them
 *  type: every keystroke that causes a render throws the caret back. */
/** MODAL AND NON-MODAL ARE DIFFERENT PROMISES, and conflating them was my first
 *  mistake here. A modal (aria-modal="true") owes the keyboard a trap: Tab must
 *  not reach the page behind, because the page behind is declared unavailable. A
 *  NON-MODAL dialog -- the timer form, the who-is-affected prompt, the token
 *  picker -- is a panel the DM can legitimately tab away from, and trapping Tab
 *  in one would be a cage around something they never said was exclusive.
 *
 *  What both owe is the other two: focus moves IN when it opens, and Escape
 *  closes it. Pass { trap: false } for the non-modal ones.
 */
export function useFocusTrap<T extends HTMLElement>(onClose?: () => void, active = true, options?: { trap?: boolean }) {
  const trapTab = options?.trap !== false;
  const ref = useRef<T>(null);
  /* Captured before the dialog can steal it. */
  const returnTo = useRef<HTMLElement | null>(null);
  const close = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    if (!active || typeof document === "undefined") return;
    returnTo.current = document.activeElement as HTMLElement | null;
    const node = ref.current;
    if (!node) return;

    const items = () => Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.offsetParent !== null || el === document.activeElement);

    /* Focus in, after the browser has laid the dialog out -- querying before
       paint finds the elements but offsetParent is not settled, so a dialog that
       animates in would be measured as having nothing focusable. */
    const t = window.setTimeout(() => { openFocus(items())?.focus(); }, 0);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && close.current) { e.preventDefault(); close.current(); return; }
      if (e.key !== "Tab" || !trapTab) return;
      const list = items();
      const at = list.indexOf(document.activeElement as HTMLElement);
      const to = nextIndex(list.length, at, e.shiftKey);
      /* Nothing to focus: leave Tab alone rather than trapping the DM in a dialog
         with no way out. */
      if (to === null) return;
      e.preventDefault();
      list[to].focus();
    };

    node.addEventListener("keydown", onKey);
    /* Also on the document, so a Tab pressed while focus has somehow escaped
       still comes back. That is the case the trap exists for. */
    const onDocKey = (e: KeyboardEvent) => {
      if (!trapTab || e.key !== "Tab" || node.contains(document.activeElement)) return;
      const list = items();
      if (!list.length) return;
      e.preventDefault();
      list[e.shiftKey ? list.length - 1 : 0].focus();
    };
    document.addEventListener("keydown", onDocKey);
    /* A non-modal panel can be left by Tab, so its Escape has to be heard from
       outside it too -- otherwise the DM has to tab back in to dismiss it. */
    const onDocEscape = (e: KeyboardEvent) => {
      if (e.key !== "Escape" || !close.current || node.contains(document.activeElement)) return;
      /* Only when nothing closer has claimed it: a field's own Escape wins. */
      if (e.defaultPrevented) return;
      close.current();
    };
    if (!trapTab) document.addEventListener("keydown", onDocEscape);

    return () => {
      window.clearTimeout(t);
      node.removeEventListener("keydown", onKey);
      document.removeEventListener("keydown", onDocKey);
      document.removeEventListener("keydown", onDocEscape);
      /* Back where it came from, but only if that is still a real place to be:
         the row the dialog was opened from may no longer exist. */
      if (canRestore(returnTo.current)) returnTo.current?.focus();
    };
  }, [active, trapTab]);

  return ref;
}
